import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MaximizeIcon, MinimizeIcon, PanelLeftIcon, PencilIcon, PinIcon, SearchIcon, SendHorizontalIcon, SlidersHorizontalIcon, SquarePenIcon, Trash2Icon, XIcon } from 'lucide-react';
import { askCarli } from '../utils/carliAgent';
import { CarliConversation, useCarliConversations } from '../hooks/useCarliConversations';
import { CarliCustomize } from './CarliCustomize';
const carliIconUrl = 'https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg';
const easeOut = [0.23, 1, 0.32, 1] as const;

// Prompts adapt to wherever the user currently is in the portal.
const promptsByView: Record<string, string[]> = {
  carli: ['What needs my attention today?', 'Where is recovery trending?', 'What should we promote next?'],
  inventory: ['Which upload is underperforming?', 'What is still sitting unsold?', 'How was this forecast built?'],
  performance: ['Why did recovery change this month?', 'Which channel recovers best?', 'What is selling fastest?'],
  marketing: ['Which channel has the best ROAS?', 'Where should the next $10K go?', 'How is the Vibe spend performing?'],
  controls: ['What index price do you recommend?', 'Should live selling stay held?', 'Which channels should be on?'],
  messages: ['Summarize what brands are asking', 'Draft a reply to this brand', 'What needs a response today?'],
  finances: ['When is the next payout?', 'What is unreconciled?', 'How much has been paid out?'],
  demand: ['Where is demand missing?', 'Which campaigns need attention?', 'What creative should we run next?'],
  pipeline: ['Which brands signed but never activated?', 'What contracts are waiting?', 'Who should I follow up with?'],
  finance: ['Which brand contributes the most margin?', 'What is our blended take rate?', 'What partner invoices are overdue?']
};
const viewLabels: Record<string, string> = {
  carli: 'Home',
  inventory: 'Inventory',
  performance: 'Recovery',
  marketing: 'Marketing',
  controls: 'Settings',
  messages: 'Messages',
  finances: 'Payouts',
  demand: 'Demand',
  pipeline: 'Pipeline',
  finance: 'Finance'
};
const styleGuidance: Record<string, string> = {
  Brief: 'Keep answers to two or three sentences unless explicitly asked for more.',
  Balanced: 'Keep answers to a short paragraph or a tight list unless asked for depth.',
  Detailed: 'Give thorough answers with the supporting numbers and reasoning laid out.'
};
export function CarliDock({
  activeView,
  selectedOwner,
  contextSummary
}: {
  activeView: string;
  selectedOwner: string;
  contextSummary?: string;
}) {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [customizing, setCustomizing] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const store = useCarliConversations();
  const messages = store.active?.messages ?? [];
  const viewLabel = viewLabels[activeView] ?? 'Portal';
  const brandLabel = selectedOwner || 'All brands';
  const prompts = promptsByView[activeView] ?? promptsByView.carli;
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking, open, expanded]);
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || thinking) return;
    const history = [...messages, {
      role: 'user' as const,
      content: trimmed
    }];
    const conversationId = store.appendMessage({
      role: 'user',
      content: trimmed
    });
    setDraft('');
    setThinking(true);
    setError(null);
    try {
      const reply = await askCarli({
        messages: history,
        context: [contextSummary ?? `The user is on the "${viewLabel}" screen. Brand scope: ${brandLabel}.`, ['HOW TO ANSWER: You have visibility into the entire portal — every brand, inventory submission, order file, marketing channel, demand campaign, message thread, and sales pipeline record listed above. Answer questions about any of it, regardless of which screen the user is on. Never tell the user to switch the brand selector or navigate elsewhere to get an answer you can already give from this context. Only say you lack a data point if it genuinely is not above.', store.preferences.displayName ? `The user goes by ${store.preferences.displayName}.` : '', styleGuidance[store.preferences.responseStyle], store.preferences.instructions ? `Standing instructions from the user: ${store.preferences.instructions}` : ''].filter(Boolean).join(' ')].filter(Boolean).join('\n\n'),
        maxTokens: store.preferences.responseStyle === 'Detailed' ? 1100 : 600
      });
      store.appendToConversation(conversationId, {
        role: 'assistant',
        content: reply
      });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Carli is unavailable right now.');
    } finally {
      setThinking(false);
    }
  };
  const filterConversations = (list: CarliConversation[]) => query.trim() ? list.filter(conversation => conversation.title.toLowerCase().includes(query.trim().toLowerCase())) : list;
  const greetingName = store.preferences.displayName ? `, ${store.preferences.displayName}` : '';
  return <>
      <AnimatePresence data-id="element-489" data-mp="1wma2gpjkijpi">
        {!open && <motion.button key="bubble" type="button" onClick={() => setOpen(true)} initial={{
        opacity: 0,
        scale: 0.8,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.8,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: easeOut,
        delay: 0.06
      }} className="fixed bottom-5 right-5 z-40 flex items-center gap-2.5 rounded-full bg-[#BB5E40] py-2.5 pl-2.5 pr-5 text-sm font-semibold text-cream shadow-lg transition-colors hover:bg-[#A34E33] focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/40 focus:ring-offset-2" data-id="element-490" data-mp="20mk9m5j3jg5y">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cream" data-id="element-491" data-mp="3hudab7pgxm3x">
              <CarliMark className="h-5 w-5" data-id="element-492" data-mp="hiw1vm6h06r" />
            </span>
            Ask Carli
          </motion.button>}
      </AnimatePresence>

      <AnimatePresence data-id="element-493" data-mp="9hfrcg32oxas">
        {open && <motion.section key="dock" aria-label="Ask Carli" initial={{
        opacity: 0,
        y: 32,
        scaleY: 0.94,
        scaleX: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scaleY: 1,
        scaleX: 1
      }} exit={{
        opacity: 0,
        y: 26,
        scaleY: 0.9,
        scaleX: 0.94
      }} transition={{
        duration: 0.26,
        ease: easeOut
      }} style={{
        transformOrigin: 'bottom right'
      }} className="fixed inset-x-0 bottom-0 z-40 border-t border-[#BB5E40]/30 bg-paper shadow-[0_-8px_32px_rgba(26,26,26,0.12)]" data-id="element-494" data-mp="9ebcmorzej5v">
            <div className="mx-auto flex max-w-[1500px] px-4 py-4 sm:px-6" style={{
          height: expanded ? 'min(88vh, 900px)' : 'min(58vh, 460px)'
        }} data-id="element-495" data-mp="5cbympq2xtai">
              {historyOpen && <div className="mr-4 hidden w-60 shrink-0 flex-col border-r border-ink/10 pr-4 lg:flex" data-id="element-496" data-mp="3rleianhrmu1n">
                  <button type="button" onClick={() => {
              store.startNew();
              setCustomizing(false);
            }} className="flex items-center gap-2 rounded-xl border border-ink/15 px-3.5 py-2.5 text-sm font-semibold transition-colors hover:border-[#BB5E40]/45 hover:bg-[#BB5E40]/[0.05]" data-id="element-497" data-mp="ds8ed4gdvcc">
                    <SquarePenIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-498" data-mp="1to9jwhhywm5" />
                    New chat
                  </button>

                  <div className="relative mt-3" data-id="element-499" data-mp="3p18bjfyhe5nq">
                    <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" aria-hidden="true" data-id="element-500" data-mp="3p2jjlryylzkn" />
                    <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search chats" aria-label="Search conversations" className="w-full rounded-xl border border-ink/12 bg-cream/40 py-2 pl-8 pr-3 text-xs focus:border-[#BB5E40] focus:outline-none" data-id="element-501" data-mp="3f9m5dy102wru" />
                  </div>

                  <div className="mt-3 min-h-0 flex-1 space-y-4 overflow-y-auto pr-1" data-id="element-502" data-mp="2wckvbysla6h1">
                    <ConversationGroup label="Pinned" conversations={filterConversations(store.ordered.pinned)} activeId={store.activeId} onSelect={id => {
                store.setActiveId(id);
                setCustomizing(false);
              }} onRename={store.renameConversation} onTogglePin={store.togglePinned} onDelete={store.deleteConversation} data-id="element-503" data-mp="1bt8pt5kmo7ib" />
                    <ConversationGroup label="Chats" conversations={filterConversations(store.ordered.recent)} activeId={store.activeId} onSelect={id => {
                store.setActiveId(id);
                setCustomizing(false);
              }} onRename={store.renameConversation} onTogglePin={store.togglePinned} onDelete={store.deleteConversation} data-id="element-504" data-mp="123ff06sb7wf4" />
                    {store.conversations.length === 0 && <p className="px-1 text-xs leading-relaxed text-ink/45" data-id="element-505" data-mp="uq4l7b9npu29">Your conversations with Carli will appear here.</p>}
                  </div>

                  <button type="button" onClick={() => setCustomizing(true)} className="mt-3 flex items-center gap-2 border-t border-ink/10 pt-3 text-xs font-semibold text-ink/60 transition-colors hover:text-[#94452E]" data-id="element-506" data-mp="20pakmlwm36v">
                    <SlidersHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-507" data-mp="gp1yqxer2hqt" />
                    Customize Carli
                  </button>
                </div>}

              <div className="flex min-w-0 flex-1 flex-col" data-id="element-508" data-mp="3keexxh253ng8">
                <div className="flex items-center justify-between gap-4 border-b border-ink/10 pb-3" data-id="element-509" data-mp="3kcu1tn06fwg9">
                  <div className="flex min-w-0 items-center gap-3" data-id="element-510" data-mp="3cu4dhjcmb29g">
                    <button type="button" onClick={() => setHistoryOpen(value => !value)} aria-label={historyOpen ? 'Hide chat history' : 'Show chat history'} className="hidden rounded-lg p-2 text-ink/50 transition-colors hover:bg-cream hover:text-ink lg:block" data-id="element-511" data-mp="37wufhfb5xia0">
                      <PanelLeftIcon className="h-4 w-4" data-id="element-512" data-mp="2p5l2ux63ide7" />
                    </button>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#BB5E40]/25 bg-cream" data-id="element-513" data-mp="2k68a5vsjmc3p">
                      <CarliMark className="h-5 w-5" data-id="element-514" data-mp="2f86or47na0p2" />
                    </span>
                    <div className="min-w-0" data-id="element-515" data-mp="2a8kff5uxt4lj">
                      <p className="truncate text-sm font-semibold" data-id="element-516" data-mp="2a796krmmx6nk">{store.active?.title ?? 'Ask Carli'}</p>
                      <p className="truncate text-xs text-ink/55" data-id="element-517" data-mp="268jxvccjkda9">
                        Viewing {viewLabel} · {brandLabel}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1" data-id="element-518" data-mp="20ic2vuw5ezqn">
                    <button type="button" onClick={() => setExpanded(value => !value)} aria-label={expanded ? 'Shrink Ask Carli' : 'Expand Ask Carli'} className="rounded-full p-2 text-ink/50 transition-colors hover:bg-cream hover:text-ink" data-id="element-519" data-mp="1mlytza5ok6ns">
                      {expanded ? <MinimizeIcon className="h-4 w-4" data-id="element-520" data-mp="25k1f3dv3xjgb" /> : <MaximizeIcon className="h-4 w-4" data-id="element-521" data-mp="25j02v62cuqx0" />}
                    </button>
                    <button type="button" onClick={() => setOpen(false)} aria-label="Close Ask Carli" className="rounded-full p-2 text-ink/50 transition-colors hover:bg-cream hover:text-ink" data-id="element-522" data-mp="2ahbdhj0h55ga">
                      <XIcon className="h-4 w-4" data-id="element-523" data-mp="3ccedhhnl45xz" />
                    </button>
                  </div>
                </div>

                {customizing ? <CarliCustomize preferences={store.preferences} onSave={store.setPreferences} onClose={() => setCustomizing(false)} data-id="element-524" data-mp="3m3922soazhml" /> : <>
                    <div ref={scrollRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto py-4" style={{
                scrollbarGutter: 'stable'
              }} data-id="element-525" data-mp="2tf4ug1m68uh9">
                      {messages.length === 0 && !thinking && <div className="mx-auto max-w-3xl" data-id="element-526" data-mp="33ec2qae1tyxi">
                          <p className="text-sm leading-relaxed text-ink/65" data-id="element-527" data-mp="2ycwk0ozcg4ia">
                            Hi{greetingName}. I can see the whole portal — inventory, recovery, demand, marketing, messages, and
                            pipeline. You are on {viewLabel} for {brandLabel}, but ask me about anything.
                          </p>
                          <div className="mt-3 flex flex-wrap gap-2" data-id="element-528" data-mp="2je3urmatg8ms">
                            {prompts.map(prompt => <button key={prompt} type="button" onClick={() => send(prompt)} className="rounded-full border border-[#BB5E40]/30 bg-[#BB5E40]/[0.06] px-3.5 py-2 text-xs font-semibold text-[#94452E] transition-colors hover:border-[#BB5E40]" data-id="element-529" data-mp="2taf7uei8d2ia">
                                {prompt}
                              </button>)}
                          </div>
                        </div>}

                      <div className="mx-auto max-w-3xl space-y-3" data-id="element-530" data-mp="3l1v28uq13tj2">
                        {messages.map((message, index) => <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-531" data-mp="3q17q9ismz6n8" data-mpi={index}>
                            <p className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${message.role === 'user' ? 'bg-[#BB5E40] text-cream' : 'border border-ink/10 bg-cream/50 text-ink/80'}`} data-id="element-532" data-mp="1eazu2j0tk3s3">
                              {message.content}
                            </p>
                          </div>)}

                        {thinking && <p className="text-sm text-ink/50" data-id="element-533" data-mp="1t43d6uu26pkx">Carli is thinking…</p>}
                        {error && <p className="text-sm text-[#94452E]" data-id="element-534" data-mp="1o9fk0io41700">{error}</p>}
                      </div>
                    </div>

                    <form onSubmit={event => {
                event.preventDefault();
                send(draft);
              }} className="mx-auto flex w-full max-w-3xl items-center gap-2 border-t border-ink/10 pt-3" data-id="element-535" data-mp="2grkz49qn6tfz">
                      <input value={draft} onChange={event => setDraft(event.target.value)} placeholder="Ask Carli anything…" aria-label="Ask Carli a question" className="min-w-0 flex-1 rounded-full border border-ink/15 bg-paper px-4 py-3 text-sm focus:border-[#BB5E40] focus:outline-none focus:ring-2 focus:ring-[#BB5E40]/20" data-id="element-536" data-mp="2zwprtuhs44v3" />
                      <button type="submit" disabled={thinking || draft.trim().length === 0} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#BB5E40] text-cream transition-colors hover:bg-[#A34E33] disabled:opacity-45" aria-label="Send" data-id="element-537" data-mp="2h3c52co7z4qk">
                        <SendHorizontalIcon className="h-4 w-4" data-id="element-538" data-mp="5e1nlxdr8ohq" />
                      </button>
                    </form>
                  </>}
              </div>
            </div>
          </motion.section>}
      </AnimatePresence>
    </>;
}
function ConversationGroup({
  label,
  conversations,
  activeId,
  onSelect,
  onRename,
  onTogglePin,
  onDelete
}: {
  label: string;
  conversations: CarliConversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onTogglePin: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  if (conversations.length === 0) return null;
  return <div data-id="element-539" data-mp="2imc5f735qvdu">
      <p className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink/40" data-id="element-540" data-mp="2im2lo18phkgl">{label}</p>
      <ul className="space-y-0.5" data-id="element-541" data-mp="2dlocgfvdc4gz">
        {conversations.map(conversation => <li key={conversation.id} className="group relative" data-id="element-542" data-mp="o933q1guse65">
            <button type="button" onClick={() => onSelect(conversation.id)} className={`w-full truncate rounded-lg py-2 pl-2.5 pr-16 text-left text-sm transition-colors ${activeId === conversation.id ? 'bg-[#BB5E40]/[0.08] font-semibold text-[#94452E]' : 'text-ink/70 hover:bg-cream/70'}`} data-id="element-543" data-mp="jajbjjbo265c">
              {conversation.title}
            </button>
            <span className="absolute right-1 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 group-hover:flex group-focus-within:flex" data-id="element-544" data-mp="c4p0d41ttm2u">
              <IconAction label={conversation.pinned ? 'Unpin chat' : 'Pin chat'} onClick={() => onTogglePin(conversation.id)} icon={<PinIcon className="h-3 w-3" data-id="element-546" data-mp="3knd57lial2il" />} data-id="element-545" data-mp="3flxkjuk10y0q" />
              <IconAction label="Rename chat" onClick={() => {
            const next = window.prompt('Rename chat', conversation.title);
            if (next) onRename(conversation.id, next);
          }} icon={<PencilIcon className="h-3 w-3" data-id="element-548" data-mp="2pbx2z339reyv" />} data-id="element-547" data-mp="3pleoa6fpx1u2" />
              <IconAction label="Delete chat" onClick={() => onDelete(conversation.id)} icon={<Trash2Icon className="h-3 w-3" data-id="element-550" data-mp="2fmdi5rvzxb1d" />} data-id="element-549" data-mp="2pbf0hoq0k4ii" />
            </span>
          </li>)}
      </ul>
    </div>;
}
function IconAction({
  label,
  onClick,
  icon
}: {
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-label={label} className="rounded p-1 text-ink/45 transition-colors hover:bg-paper hover:text-ink" data-id="element-551" data-mp="26n9zgp0qqxne">
      {icon}
    </button>;
}
function CarliMark({
  className
}: {
  className?: string;
}) {
  return <span aria-hidden="true" className={`inline-block shrink-0 bg-[#BB5E40] ${className ?? ''}`} style={{
    WebkitMaskImage: `url(${carliIconUrl})`,
    maskImage: `url(${carliIconUrl})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain'
  }} data-id="element-552" data-mp="1rphjxwv72997" />;
}